import type {
    LabsMessageToPlatform,
    LabsResource,
    LabsResourceID,
} from "@/client/core/platform-types/labs.api";
import { LabsMessageToPlatformType } from "@/client/core/platform-types/labs.api";
import { createSignalWithValue } from "@/client/utils/signal";

/**
 * Sidepanel-host resource export: mirrors client-side resources (which live in
 * memory + IndexedDB) onto the host's disk so they can be revealed in
 * Finder/Explorer and dragged into the DAW. Kept as module state — like
 * auth-token.signal — because the bridge and the resources service are wired
 * at different times.
 */

export const exportedResourceIdsSignal = createSignalWithValue<ReadonlySet<string>>(new Set());

type SendToHost = (message: LabsMessageToPlatform) => void;

let sendToHost: SendToHost | undefined;
const pendingExports = new Map<string, LabsResource>();
const inFlightExports = new Set<string>();

const isExportableType = (id: LabsResourceID): boolean => id.type === "wav" || id.type === "mid";

const sendExport = (resource: LabsResource): void => {
    inFlightExports.add(resource.id.value);
    sendToHost?.({
        type: LabsMessageToPlatformType.ExportResource,
        id: resource.id,
        file: resource.file,
    });
};

/** Registered by the sidepanel bridge once the host port is open. */
export const registerSidepanelSend = (fn: SendToHost): void => {
    sendToHost = fn;
    for (const resource of pendingExports.values()) {
        sendExport(resource);
    }
    pendingExports.clear();
};

/**
 * Queues a resource for export to the host. Idempotent: resources already
 * exported or in flight are skipped, and exports before the handshake are
 * buffered until the port opens.
 */
export const exportResourceToHost = (resource: LabsResource): void => {
    const idValue = resource.id.value;
    if (
        !isExportableType(resource.id) ||
        exportedResourceIdsSignal.value.has(idValue) ||
        inFlightExports.has(idValue) ||
        pendingExports.has(idValue)
    ) {
        return;
    }
    if (sendToHost === undefined) {
        pendingExports.set(idValue, resource);
        return;
    }
    sendExport(resource);
};

const revealWhenExported = new Set<string>();

/** Called by the bridge when the host confirms a file is on disk. */
export const markResourceExported = (id: LabsResourceID): void => {
    inFlightExports.delete(id.value);
    exportedResourceIdsSignal.notify(new Set([...exportedResourceIdsSignal.value, id.value]));
    if (revealWhenExported.delete(id.value)) {
        sendToHost?.({ type: LabsMessageToPlatformType.RevealResourceOnDisk, id });
    }
};

/**
 * Starts a native OS drag of the resource via the host (the caller cancels
 * the HTML5 drag). The host serializes against any in-flight export of the
 * same resource, so drag-after-export races resolve host-side; if the
 * resource was never exported, export it now so the NEXT drag works.
 */
export const startSidepanelResourceDrag = (resource: LabsResource): void => {
    if (
        exportedResourceIdsSignal.value.has(resource.id.value) ||
        inFlightExports.has(resource.id.value)
    ) {
        sendToHost?.({ type: LabsMessageToPlatformType.StartNativeDrag, id: resource.id });
        return;
    }
    exportResourceToHost(resource);
    sendToHost?.({ type: LabsMessageToPlatformType.StartNativeDrag, id: resource.id });
};

/**
 * Reveal a resource in Finder/Explorer via the host, exporting it on demand
 * first if it is not on the host's disk yet.
 */
export const revealSidepanelResource = (resource: LabsResource): void => {
    if (exportedResourceIdsSignal.value.has(resource.id.value)) {
        sendToHost?.({
            type: LabsMessageToPlatformType.RevealResourceOnDisk,
            id: resource.id,
        });
        return;
    }
    revealWhenExported.add(resource.id.value);
    exportResourceToHost(resource);
};
